import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { SendSummaryComponent } from '../../components';
import { PYGEditSidebar } from '../../components/PYGEditSidebar/PYGEditSidebar';
import { PICK_CAMPAIGN_IDS } from '../../constants/campaigns';
import { endpoints, routes } from '../../constants/routing';
import { EMAIL_TEMPLATE_ID, MESSAGE } from '../../constants/templates';
import { useCurrency } from '../../contexts/CurrencyProvider';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import useFetch from '../../hooks/useFetch';
import { fetchBusinessReasonsRequest } from '../../store/actions/reasons';
import { fetchReportByIdRequest, populateReportCampaignSummariesRequest } from '../../store/actions/reports';
import { selectIsSendEditAllowedByRole } from '../../store/selectors/auth';
import { selectDeliveryReportById, selectIsSingleDeliveryReportsFetching } from '../../store/selectors/reports';
import { IEditSendFieldRequestPayload } from '../../types/bucket';
import { ICampaign } from '../../types/campaigns';
import { EditSendModeEnum, ReportStatusEnum } from '../../types/reports';
import { IReportsRouteParams } from '../../types/routing';
import { NotificationListEnum } from '../../types/shell';
import { isSameFC } from '../../utils/inventories';
import notification from '../../utils/notification';
import {
  isAwaitingAddress,
  isCampaignPicked,
  isCustomReport,
  isDigitalPYGReport,
  isDigitalReport,
  isPYGReport,
  isValidScheduledEngagement,
} from '../../utils/reports';
import { handleApiError } from '../../utils/ui';
import EditSendSidebar from '../EditSendSidebar/EditSendSidebar';

import styles from './ReportSummaryContainer.module.scss';

const ReportSummaryContainer: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { reportId, type } = useParams<IReportsRouteParams>();
  const { setAction, setTitle } = useHeaderDispatch();
  const { getSendTotalPrice } = useCurrency();

  const isEditAllowedByRole = useSelector(selectIsSendEditAllowedByRole);

  const [isEditSidebarShown, setIsEditSidebarShown] = useState<EditSendModeEnum | undefined>();
  const [draftCampaigns, setDraftCampaigns] = useState<ICampaign[] | null>(null);

  const { make: editSendFieldRequest, isLoading: isSavingOrder } = useFetch<IEditSendFieldRequestPayload, never>({
    endpoint: endpoints.editSendField,
    showToastOnError: true,
    method: 'PATCH',
    useMonolithApi: true,
  });

  const report = useSelector(selectDeliveryReportById(reportId, type));
  const isLoadingSingle = useSelector(selectIsSingleDeliveryReportsFetching);

  const isDigital = useMemo(() => !!report && isDigitalReport(report), [report]);
  const isDigitalPYG = useMemo(() => !!report && isDigitalPYGReport(report), [report]);

  const isEditingEngagementAllowed = useMemo(() => {
    if (!report) {
      return false;
    }

    const isEngagementCancelled = report.status === ReportStatusEnum.Canceled;
    const isPYG = isPYGReport(report);
    const isUnpickedPYG = isPYG && !isCampaignPicked(report);
    const hasItemsFromDifferentFC = !isSameFC(report.items || []);

    // Hard blocks that apply to everything
    if (!isEditAllowedByRole || isDigital || isDigitalPYG || isEngagementCancelled) {
      return false;
    }

    // Unpicked PYG: campaign not yet chosen, so item/FC checks don't apply
    if (isUnpickedPYG) {
      return report.status === ReportStatusEnum.AwaitingAddress || report.status === ReportStatusEnum.Viewed;
    }

    // Content check: campaigns for PYG, items otherwise
    const hasContent = isPYG ? !!report.pick_campaign_ids?.length : !!report.items?.length;
    if (!hasContent || hasItemsFromDifferentFC) {
      return false;
    }

    return (
      isValidScheduledEngagement(report) ||
      report.status === ReportStatusEnum.Viewed ||
      report.status === ReportStatusEnum.AwaitingShipment ||
      report.status === ReportStatusEnum.AwaitingAddress
    );
  }, [isEditAllowedByRole, report, isDigital, isDigitalPYG]);

  const isDelayedShipping = useMemo(() => !!report && isAwaitingAddress(report), [report]);

  const showEmailSection = useMemo(() => {
    return (
      typeof report?.[EMAIL_TEMPLATE_ID] !== 'undefined' &&
      report?.[EMAIL_TEMPLATE_ID] &&
      (isDigital || isDelayedShipping)
    );
  }, [report, isDigital, isDelayedShipping]);

  const fetchReport = useCallback(() => {
    if (reportId) {
      return new Promise((resolve, reject) => {
        dispatch(
          fetchReportByIdRequest({
            reportId,
            type,
            resolve,
            reject,
          }),
        );
      }).catch(
        handleApiError(`Something bad happened during the delivery report retrieval.`, () =>
          history.push(routes.reports.root),
        ),
      );
    }
  }, [dispatch, history, type, reportId]);

  const fetchReasons = useCallback(() => {
    dispatch(fetchBusinessReasonsRequest());
  }, [dispatch]);

  const fetchCampaignSummaries = useCallback(
    (campaignIds: string[]) => {
      if (reportId) {
        dispatch(populateReportCampaignSummariesRequest({ campaign_ids: campaignIds, reportId, type }));
      }
    },
    [dispatch, reportId, type],
  );

  const isPYGEditOpen = useMemo(() => isEditSidebarShown === EditSendModeEnum.PickCampaigns, [isEditSidebarShown]);

  useEffect(() => {
    if (isPYGEditOpen && draftCampaigns === null && report?.pick_campaigns) {
      setDraftCampaigns(report.pick_campaigns as ICampaign[]);
    }
  }, [isPYGEditOpen, draftCampaigns, report?.pick_campaigns]);

  const handleCloseEditSidebar = useCallback(() => {
    setIsEditSidebarShown(undefined);
    setDraftCampaigns(null);
  }, []);

  const handleReorderCampaigns = useCallback((nextOrder: ICampaign[]) => {
    setDraftCampaigns(nextOrder);
  }, []);

  const isOrderDirty = useMemo(() => {
    if (!draftCampaigns || !report?.pick_campaign_ids) {
      return false;
    }
    return draftCampaigns.some((c, i) => c.box_id !== report.pick_campaign_ids[i]);
  }, [draftCampaigns, report?.pick_campaign_ids]);

  const handleSaveOrder = useCallback(() => {
    if (!reportId || !draftCampaigns || isSavingOrder) {
      return;
    }
    editSendFieldRequest({
      pathParams: {
        id: reportId,
      },
      body: {
        key: PICK_CAMPAIGN_IDS,
        value: draftCampaigns.map((c) => c.box_id),
      },
    })
      .then(() => {
        notification.success(NotificationListEnum.Success, { content: 'Campaign order updated.' });
        setDraftCampaigns(null);
        setIsEditSidebarShown(undefined);
        fetchReport();
      })
      .catch(handleApiError('Failed to update campaign order.'));
  }, [reportId, draftCampaigns, isSavingOrder, editSendFieldRequest, fetchReport]);

  useEffect(() => {
    if (!report) {
      fetchReport();
    }
  }, [report, fetchReport]);

  useEffect(() => {
    const isCustom = !!report && isCustomReport(report);
    const isLoadedSummaries = typeof report?.pick_campaigns !== `undefined`;
    const isPYG = !!report && isPYGReport(report);

    if (report && !isLoadedSummaries && !isLoadingSingle && !isCustom && isPYG) {
      fetchCampaignSummaries(report.pick_campaign_ids);
    }
  }, [report, fetchCampaignSummaries, isLoadingSingle]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  useEffect(() => {
    setTitle?.('Send summary');
    setAction?.(() => history.push(routes.reports.root));
  }, [setTitle, setAction, history]);

  const sidebarCampaigns = useMemo(
    () => draftCampaigns ?? (report?.pick_campaigns as ICampaign[] | undefined) ?? [],
    [draftCampaigns, report?.pick_campaigns],
  );

  return (
    <div className={styles.container}>
      <SendSummaryComponent
        type={type}
        instance={report}
        showNote={!!report?.[MESSAGE]}
        showEmail={!!showEmailSection}
        total={getSendTotalPrice(report)}
        isDigital={isDigital || isDigitalPYG}
        isPYG={!!report && isPYGReport(report)}
        isEditable={isEditingEngagementAllowed}
        onEdit={setIsEditSidebarShown}
        showQuantity
        reportMode
      />
      {isEditingEngagementAllowed && !isPYGEditOpen && (
        <EditSendSidebar
          mode={isEditSidebarShown}
          report={report}
          onClose={handleCloseEditSidebar}
          onSave={fetchReport}
        />
      )}
      {isPYGEditOpen && sidebarCampaigns.length > 0 && (
        <PYGEditSidebar
          campaigns={sidebarCampaigns}
          onReorder={handleReorderCampaigns}
          onCancel={handleCloseEditSidebar}
          onSave={handleSaveOrder}
          saveLabel="Save"
          isSaveDisabled={!isOrderDirty}
          isSaving={isSavingOrder}
        />
      )}
    </div>
  );
};

export default ReportSummaryContainer;
